import { useCallback, useEffect, useMemo } from 'react';

/**
 * Task page tabs in display order. The tab key doubles as the `?action=`
 * query value, so pre-tabs deep links (`action=model-setup`,
 * `action=data-selection`, `action=evaluation-from-session`) land on the
 * matching tab with no extra mapping. `data-overview` and `model-details`
 * (Versions) are tab-only values. Legacy `action=train` (removed Model
 * Summary tab) is dropped like any other unknown value.
 *
 * NOTE: the page-level tab state intentionally stays on `action` — the `tab`
 * query param is already used by the Data Editor's internal tabs
 * (`train-set` / `benchmark` / ...) for deep links.
 */
export const TASK_PAGE_TABS = [
    { key: 'data-overview', label: 'Data Overview' },
    { key: 'model-setup', label: 'Model Configuration' },
    { key: 'data-selection', label: 'Data Editor' },
    { key: 'evaluation-from-session', label: 'Evaluations' },
    { key: 'model-details', label: 'Versions' },
    { key: 'inference', label: 'Inference' },
];

export const DEFAULT_TASK_TAB = TASK_PAGE_TABS[0].key;

const TASK_PAGE_TAB_KEYS = new Set(TASK_PAGE_TABS.map((t) => t.key));

/** Legacy `?action=playground` opened the Playground drawer over the task landing. */
const LEGACY_PLAYGROUND_ACTION = 'playground';

/**
 * Keeps Task page URL (`task_id`, `action` = active tab, `playground`) and
 * local state in sync.
 *
 * The URL is the source of truth for refresh/back-forward behavior: the
 * active tab comes from `?action=` (default Data Overview) and the Playground
 * drawer from `?playground=true`.
 */
export const useTaskActionUrlState = ({
    searchParams,
    setSearchParams,
    usecases,
    activeUsecaseId,
    setCurrentUsecaseId,
}) => {
    const actionFromUrl = searchParams.get('action');
    const isLegacyPlaygroundAction = actionFromUrl === LEGACY_PLAYGROUND_ACTION;

    const activeTabKey = useMemo(() => {
        if (actionFromUrl && TASK_PAGE_TAB_KEYS.has(actionFromUrl)) {
            return actionFromUrl;
        }
        return DEFAULT_TASK_TAB;
    }, [actionFromUrl]);

    const isPlaygroundOpen =
        searchParams.get('playground') === 'true' || isLegacyPlaygroundAction;

    const updateTaskSearchParams = useCallback((updates, options = { replace: true }) => {
        const next = new URLSearchParams(searchParams);
        if (Object.prototype.hasOwnProperty.call(updates, 'task_id')) {
            if (updates.task_id) {
                next.set('task_id', updates.task_id);
                next.delete('objective');
            } else {
                next.delete('task_id');
                next.delete('objective');
            }
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'action')) {
            if (updates.action) {
                next.set('action', updates.action);
            } else {
                next.delete('action');
            }
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'playground')) {
            if (updates.playground) {
                next.set('playground', 'true');
            } else {
                next.delete('playground');
            }
        }
        if (Object.prototype.hasOwnProperty.call(updates, 'fromEvaluation')) {
            if (updates.fromEvaluation) {
                next.set('fromEvaluation', 'true');
            } else {
                next.delete('fromEvaluation');
            }
        }
        if (next.toString() === searchParams.toString()) return;
        setSearchParams(next, { replace: options?.replace ?? true });
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (!usecases.length) return;
        const legacyObjective = searchParams.get('objective');
        const urlTaskId = searchParams.get('task_id') || legacyObjective;
        const isValidTaskId = urlTaskId && usecases.some((u) => u._id === urlTaskId);
        if (isValidTaskId) {
            if (legacyObjective && !searchParams.get('task_id')) {
                const next = new URLSearchParams(searchParams);
                next.set('task_id', legacyObjective);
                next.delete('objective');
                setSearchParams(next, { replace: true });
            }
            return;
        }
        const fallbackTaskId = usecases[0]?._id;
        if (fallbackTaskId) {
            updateTaskSearchParams({ task_id: fallbackTaskId });
        }
    }, [searchParams, setSearchParams, updateTaskSearchParams, usecases]);

    // Normalize the action param: legacy `playground` becomes the dedicated
    // `playground` flag (over the default tab); unknown values are dropped.
    useEffect(() => {
        if (!actionFromUrl) return;
        if (isLegacyPlaygroundAction) {
            updateTaskSearchParams({ action: null, playground: true });
            return;
        }
        if (!TASK_PAGE_TAB_KEYS.has(actionFromUrl)) {
            updateTaskSearchParams({ action: null });
        }
    }, [actionFromUrl, isLegacyPlaygroundAction, updateTaskSearchParams]);

    /**
     * Switch to a tab (optionally for a specific objective).
     * `options.fromEvaluation` marks the Data Editor improvement mode.
     */
    const openTab = useCallback((tabKey, usecaseId, options = {}) => {
        if (!TASK_PAGE_TAB_KEYS.has(tabKey)) return;
        if (usecaseId) {
            setCurrentUsecaseId(usecaseId);
        }
        updateTaskSearchParams({
            task_id: usecaseId || activeUsecaseId || null,
            action: tabKey === DEFAULT_TASK_TAB ? null : tabKey,
            fromEvaluation: Boolean(options.fromEvaluation),
        });
    }, [activeUsecaseId, setCurrentUsecaseId, updateTaskSearchParams]);

    const openPlayground = useCallback(() => {
        updateTaskSearchParams({ playground: true });
    }, [updateTaskSearchParams]);

    const closePlayground = useCallback(() => {
        updateTaskSearchParams({ playground: false });
    }, [updateTaskSearchParams]);

    return {
        activeTabKey,
        isPlaygroundOpen,
        updateTaskSearchParams,
        openTab,
        openPlayground,
        closePlayground,
    };
};
